import React, { useRef, ChangeEvent, useState } from 'react';
import { Alert } from '@material-ui/lab';
import { observer } from 'mobx-react';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TableSortLabel,
    Snackbar,
    Tooltip,
    Typography
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import licenseImportStore from '../../stores/license/licenseImportStore';
import { LicenseData } from '../../stores/license/licenseImportStore';
import './import.css';

const LicenseImport: React.FC = observer(() => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setLoading(true);
            licenseImportStore.handleFileChange(file, () => {
                setLoading(false);
                const invalidRecords = licenseImportStore.licenseData.filter(record => !record.isValid);
                if (invalidRecords.length > 0) {
                    setSnackbarMessage(`There are ${invalidRecords.length} invalid records. Please correct them.`);
                    setSnackbarOpen(true);
                }
                // Reset the file input value to allow re-uploading the same file
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            });
        }
    };

    const handleChooseFile = () => {
        fileInputRef.current?.click();
    };

    const handleConfirm = async () => {
        setIsUploading(true);
        await licenseImportStore.uploadLicenseData(licenseImportStore.licenseData);
        if (licenseImportStore.isUploadSuccessful) {
            setSnackbarMessage('Data has been successfully uploaded and saved.');
        } else {
            setSnackbarMessage('An error occurred while saving the data. Please try again.');
        }
        setSnackbarOpen(true);
        setIsUploading(false);
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        licenseImportStore.setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        licenseImportStore.setRowsPerPage(parseInt(event.target.value, 10));
        licenseImportStore.setPage(0);
    };

    const handleRequestSort = (property: keyof LicenseData) => {
        const isAsc = licenseImportStore.orderBy === property && licenseImportStore.order === 'asc';
        licenseImportStore.setOrder(isAsc ? 'desc' : 'asc');
        licenseImportStore.setOrderBy(property);
    };

    const sortedData = licenseImportStore.licenseData.sort((a, b) => {
        // @ts-ignore
        if (a[licenseImportStore.orderBy] < b[licenseImportStore.orderBy]) {
            return licenseImportStore.order === 'asc' ? -1 : 1;
        }
        // @ts-ignore
        if (a[licenseImportStore.orderBy] > b[licenseImportStore.orderBy]) {
            return licenseImportStore.order === 'asc' ? 1 : -1;
        }
        return 0;
    });

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    return (
        <div>
            <Typography variant="h4" component="h1" gutterBottom>
                License Data Import
            </Typography>
            <Typography variant="body1" gutterBottom>
                This page is used to import data used for generating license forms using an XLSX file. Please choose a file to upload and review the data before confirming.
            </Typography>
            <hr />
            <input
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
            />
            <Button variant="contained" color="primary" onClick={handleChooseFile} style={{ marginTop: 20 }}>
                Choose File
            </Button>
            {loading ? (
                <Skeleton variant="rect" width="100%" height={400} />
            ) : (
                licenseImportStore.isPreview && (
                    <>
                        <TableContainer component={Paper} className="table-container">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {['Name', 'Email', 'Title', 'Department', 'ManagerName', 'ManagerEmail'].map((column) => (
                                            <TableCell key={column}>
                                                <TableSortLabel
                                                    active={licenseImportStore.orderBy === column}
                                                    direction={licenseImportStore.orderBy === column ? licenseImportStore.order : 'asc'}
                                                    onClick={() => handleRequestSort(column as keyof LicenseData)}
                                                >
                                                    {column}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedData.slice(licenseImportStore.page * licenseImportStore.rowsPerPage, licenseImportStore.page * licenseImportStore.rowsPerPage + licenseImportStore.rowsPerPage).map((row, index) => (
                                        <Tooltip key={index} title={row.isValid === false ? row.validationError || '' : ''} arrow>
                                            <TableRow style={{ backgroundColor: row.isValid === false ? '#ffcccc' : 'inherit' }}>
                                                <TableCell>{row.Name}</TableCell>
                                                <TableCell>{row.Email}</TableCell>
                                                <TableCell>{row.Title}</TableCell>
                                                <TableCell>{row.Department}</TableCell>
                                                <TableCell>{row.ManagerName}</TableCell>
                                                <TableCell>{row.ManagerEmail}</TableCell>
                                            </TableRow>
                                        </Tooltip>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={licenseImportStore.licenseData.length}
                                rowsPerPage={licenseImportStore.rowsPerPage}
                                page={licenseImportStore.page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableContainer>
                        {licenseImportStore.licenseData.every(record => record.isValid) && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirm}
                                className="confirm-button"
                                disabled={isUploading || licenseImportStore.isUploadSuccessful}
                            >
                                Confirm and save
                            </Button>
                        )}
                    </>
                )
            )}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={60000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={licenseImportStore.isUploadSuccessful ? 'info' : 'error'}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
});

export default LicenseImport;